/**
 * transactionForm (#1315 subtask 05 pt2) — pure helpers behind
 * TransactionFormModal. Kept out of the component so the de-DE amount
 * parsing and validation are unit-testable without rendering.
 */
import type { PlannedTransactionInput } from './optimisticPlanned';
import {
  CATEGORY_META,
  L1_GROUPS,
  LEDGER_CATEGORIES,
  crosswalkDomainToCategory,
  type LedgerCategory,
  type LedgerGroup,
} from './ledgerCategory';

export interface TransactionFormState {
  derived_fact_id?: string; // present ⇒ edit/move
  ledger_category: LedgerCategory;
  direction: 'income' | 'expense';
  label: string;
  /** Volume/quantity (de-DE text as typed); ``1`` for an amount-only line. */
  qty: string;
  /** Price per unit (de-DE text as typed). The total = qty × unit_price. */
  unit_price: string;
  value_date: string; // 'YYYY-MM-DD'
  /** Free-text note (rationale / deviation), as typed. */
  notes: string;
  /** Carried through untouched on edit — the upsert overwrites fact_value
   *  wholesale, so dropping it here would silently delete the qty metadata. */
  qty_meta?: { ha?: number; t?: number; eur_per_t?: number };
  /** The owning bank account (#2211). Set only when the modal renders the Konto
   *  select (multi-account tenants). On edit it is ALWAYS re-sent — the wholesale
   *  overwrite means omission clears the account (a deliberate un-assign back to
   *  the D2 read-side fallback), never an accidental drop. Absent on single-account
   *  tenants so ``buildPlannedInput`` keeps the wire key absent. */
  account_id?: string;
  /** The picked Gegenpartei (#2678 c) — an ``organization`` entity id. Seeded from
   *  the edited row so an edit that never opens the picker still RE-SENDS it (the
   *  upsert overwrites fact_value wholesale, so omitting it would silently unlink);
   *  cleared to ``undefined``/``''`` by the picker's "Entfernen", which is how an
   *  un-link is expressed. */
  counterparty_id?: string;
}

/** ``qty × unit_price`` rounded to cents, or ``null`` if either side is invalid.
 *  qty parses with the quantity parser (3 decimals) — the same as
 *  ``buildPlannedInput`` — so the modal preview equals the saved amount for a
 *  3-decimal volume (``1,234`` t, not silently truncated to ``1,23``; #1496). */
export function computeTotal(qty: string, unitPrice: string): number | null {
  const q = parseDeQuantity(qty);
  const p = parseDeAmount(unitPrice);
  if (q == null || p == null) return null;
  return Math.round(q * p * 100) / 100;
}

/** Valid plan-date window: ``(minExclusive, max]`` — the same range
 *  ``computeBalanceSeries`` projects, so a row outside it would be filtered
 *  from the curve yet still shown in the ledger (a phantom row). */
export interface DateBounds {
  minExclusive: string; // typically config.anchor_date
  max: string; // projection horizon end
}

/** de-DE number core: dot = thousands separator, comma = decimal (the only
 *  correct reading in a German-labelled field). ``null`` for empty / non-numeric
 *  / ≤ 0; otherwise rounded to ``decimals`` places. */
function parseDeNumber(input: string, decimals: number): number | null {
  const cleaned = input
    .replace(/[€\s]/g, '')
    .replace(/\./g, '') // strip thousands separators
    .replace(',', '.'); // decimal comma → point
  if (cleaned === '') return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n) || n <= 0) return null;
  const f = 10 ** decimals;
  return Math.round(n * f) / f;
}

/**
 * Parse a de-DE brutto money amount (2 decimals = cents). Used for prices.
 */
export function parseDeAmount(input: string): number | null {
  return parseDeNumber(input, 2);
}

/**
 * Parse a de-DE quantity (volume). Keeps up to 3 decimals — farm volumes
 * (tonnes) are finer than cents and the UI formatter renders 3 — so ``1,234`` t
 * is not silently rounded down to ``1,23``, which would corrupt qty × price.
 */
export function parseDeQuantity(input: string): number | null {
  return parseDeNumber(input, 3);
}

export interface BuildResult {
  ok: boolean;
  input?: PlannedTransactionInput;
  errors: Partial<Record<'label' | 'qty' | 'unit_price' | 'value_date', string>>;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function buildPlannedInput(state: TransactionFormState, bounds?: DateBounds): BuildResult {
  const errors: BuildResult['errors'] = {};
  const label = state.label.trim();
  if (!label) errors.label = 'Bitte eine Bezeichnung eingeben.';
  const qty = parseDeQuantity(state.qty);
  if (qty == null) errors.qty = 'Bitte eine Menge größer als 0 eingeben.';
  const unitPrice = parseDeAmount(state.unit_price);
  if (unitPrice == null) errors.unit_price = 'Bitte einen Preis größer als 0 eingeben.';
  if (!ISO_DATE.test(state.value_date)) {
    errors.value_date = 'Bitte ein Datum wählen.';
  } else if (bounds && !(state.value_date > bounds.minExclusive && state.value_date <= bounds.max)) {
    // Outside the projected window ⇒ it would vanish from the curve but linger
    // in the ledger. Keep the plan inside the forecast horizon.
    errors.value_date = 'Datum liegt außerhalb des Planungszeitraums.';
  }

  if (Object.keys(errors).length > 0) return { ok: false, errors };

  // Total is volume × price (the task's money rule). qty/unit_price ride along
  // so the ledger shows the breakdown and an edit pre-fills both sides.
  const amount = Math.round((qty as number) * (unitPrice as number) * 100) / 100;
  const notes = state.notes.trim();

  return {
    ok: true,
    errors: {},
    input: {
      derived_fact_id: state.derived_fact_id,
      ledger_category: state.ledger_category,
      direction: state.direction,
      label,
      amount_eur: amount,
      qty: qty as number,
      unit_price_eur: unitPrice as number,
      value_date: state.value_date,
      period_ym: state.value_date.slice(0, 7), // re-derived so a move updates the month
      // Omit when empty so clearing the field on edit removes the note (the
      // upsert overwrites fact_value wholesale).
      ...(notes ? { notes } : {}),
      ...(state.qty_meta ? { qty_meta: state.qty_meta } : {}),
      // #2211: emit the owning Konto when the form carries one (multi-account); a
      // single-account form has none → the key stays ABSENT (backend extra=forbid).
      ...(state.account_id ? { account_id: state.account_id } : {}),
      // #2678 c: emit the picked Gegenpartei; an unpicked/cleared one keeps the key
      // ABSENT (backend extra=forbid), which is also how an un-link reaches the wire
      // — the wholesale fact_value overwrite drops what this build omits.
      ...(state.counterparty_id ? { counterparty_id: state.counterparty_id } : {}),
    },
  };
}

/**
 * The user-facing message for a FAILED plan save (#3152). Before this a
 * rejected upsert was silent: the page closes the modal only on success and
 * the mutation's ``onError`` only rolls the optimistic cache back. Generic
 * German copy always; the server's ``detail`` is appended ONLY when it is a
 * plain string (a 502's ``planned_transaction_write_failed``) — a 422's
 * Pydantic detail is an ARRAY of error objects and must never render as raw
 * JSON at the farmer.
 */
export function planSaveErrorMessage(err: unknown): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } } | null | undefined)
    ?.response?.data?.detail;
  return typeof detail === 'string' && detail !== ''
    ? `Speichern fehlgeschlagen: ${detail}`
    : 'Speichern fehlgeschlagen. Bitte versuchen Sie es erneut.';
}

// ── Category picker (#1612) — the grouped 2-level ledger_category selector ──

export interface PickerOption {
  id: LedgerCategory;
  label: string;
  /** True when this option is in the crosswalk shortlist for the active prior. */
  suggested: boolean;
}

export interface PickerGroup {
  id: LedgerGroup;
  label: string;
  options: PickerOption[];
}

/** A category is liquidity-relevant (surfaced in the picker) when its cash/accrual
 *  flag is ⚖️ both or 💶 cash-only; accrual-only (📊) lines never move cash, so
 *  they are excluded from the liquidity-view picker (§4.6). */
function isLiquidityRelevant(id: LedgerCategory): boolean {
  return CATEGORY_META[id].view !== 'accrual_only';
}

/** A category matches the chosen sign when its direction is that sign or ``both``. */
function matchesDirection(id: LedgerCategory, direction: 'income' | 'expense'): boolean {
  const want = direction === 'income' ? 'in' : 'out';
  const d = CATEGORY_META[id].direction;
  return d === want || d === 'both';
}

/**
 * Build the grouped, direction-filtered category options for the picker
 * (level 0 = the in/out sign; below it L1 group → L2 category). Only
 * liquidity-relevant categories whose direction matches the sign are shown;
 * the not-yet-categorised ``unsorted`` bucket is always offered last. When a
 * ``priorDomain`` (an already-classified document's 9-domain id) is known, its
 * crosswalk shortlist is flagged ``suggested`` so the UI can float it.
 *
 * ``includeCategory`` keeps a specific category visible even when the sign /
 * liquidity filter would drop it — used on EDIT so the controlled ``<select>``
 * always has a matching option for the row's stored value (e.g. an accrual-only
 * category seeded out-of-band), instead of silently showing the first option
 * while the state still holds the unrendered value.
 */
export function categoriesForPicker(
  direction: 'income' | 'expense',
  priorDomain?: string,
  includeCategory?: LedgerCategory,
): PickerGroup[] {
  // Crosswalk shortlist with rank (head = 0); lower rank floats higher.
  const shortlist = priorDomain ? crosswalkDomainToCategory.shortlist(priorDomain) : [];
  const rank = new Map<LedgerCategory, number>(shortlist.map((id, i) => [id, i]));
  const visible = LEDGER_CATEGORIES.filter(
    (id) =>
      id !== 'unsorted' &&
      ((isLiquidityRelevant(id) && matchesDirection(id, direction)) || id === includeCategory),
  );

  const groups: PickerGroup[] = [];
  for (const g of L1_GROUPS) {
    const options = visible
      .filter((id) => CATEGORY_META[id].group === g.id)
      .map((id) => ({ id, label: CATEGORY_META[id].label, suggested: rank.has(id) }));
    // Float the suggested (crosswalk) options to the top of the group, in
    // crosswalk order (head first); non-suggested keep config order below.
    // Both-non-suggested must compare EQUAL (return 0) — ``Infinity - Infinity``
    // is NaN, which makes Array.sort's order engine-defined.
    options.sort((a, b) => {
      const ra = rank.has(a.id) ? rank.get(a.id)! : Infinity;
      const rb = rank.has(b.id) ? rank.get(b.id)! : Infinity;
      if (ra === rb) return 0;
      return ra - rb;
    });
    if (options.length > 0) groups.push({ id: g.id, label: g.label, options });
  }

  // Always offer the explicit not-yet-categorised bucket so a manual/unmatched
  // line can be left uncategorised. It belongs to the ``crosscutting`` L1 group
  // — append it to that group when present (e.g. ``vat`` already created it),
  // else create the group. A separate ``crosscutting`` group here would collide
  // with the loop's group (duplicate id → duplicate React ``optgroup`` key).
  const unsortedOption = { id: 'unsorted' as LedgerCategory, label: CATEGORY_META.unsorted.label, suggested: false };
  const crosscutting = groups.find((grp) => grp.id === 'crosscutting');
  if (crosscutting) {
    crosscutting.options.push(unsortedOption);
  } else {
    groups.push({ id: 'crosscutting', label: 'Übergreifend', options: [unsortedOption] });
  }
  return groups;
}

/** The full crosswalk shortlist for a prior domain (the “Vorschlag aus
 *  Dokument-Klasse …” hint), filtered to options actually offered for the sign. */
export function suggestedCategories(
  direction: 'income' | 'expense',
  priorDomain?: string,
): LedgerCategory[] {
  if (!priorDomain) return [];
  return crosswalkDomainToCategory
    .shortlist(priorDomain)
    .filter((id) => isLiquidityRelevant(id) && matchesDirection(id, direction));
}
